import React, { useEffect, useRef } from 'react';
import { createChart, ColorType } from 'lightweight-charts';

export const PriceChart = ({ data, isDarkMode, targetCoin }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  
  // 마지막 데이터의 시간을 기억하는 변수 (중복 업데이트 방지용)
  const lastTimeRef = useRef(0);

  // 1. 차트 틀(Skeleton) 생성 (코인이나 테마가 바뀔 때만 실행)
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // 기존 차트 파괴 (Cleanup)
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      seriesRef.current = null;
      lastTimeRef.current = 0; // [중요] 코인이 바뀌면 시간 기록도 리셋!
    }

    const themeColors = {
      background: 'transparent',
      textColor: isDarkMode ? '#94a3b8' : '#64748b',
      gridColor: isDarkMode ? '#334155' : '#e2e8f0',
      lineColor: isDarkMode ? '#3b82f6' : '#e11d48',
      areaTop: isDarkMode ? 'rgba(59, 130, 246, 0.4)' : 'rgba(225, 29, 72, 0.2)',
      areaBottom: isDarkMode ? 'rgba(59, 130, 246, 0.0)' : 'rgba(225, 29, 72, 0.0)',
    };

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: themeColors.background },
        textColor: themeColors.textColor,
        fontFamily: "'Inter', sans-serif",
      },
      localization: {
        locale: 'ko-KR',
        timeFormatter: (timestamp) => {
          return new Date(timestamp * 1000).toLocaleString('ko-KR', {
            timeZone: 'Asia/Seoul',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false,
          });
        },
      },
      grid: {
        vertLines: { color: 'transparent' },
        horzLines: { color: themeColors.gridColor, style: 1 },
      },
      width: chartContainerRef.current.clientWidth,
      height: 450,
      timeScale: {
        borderColor: themeColors.gridColor,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time) => {
          const date = new Date(time * 1000);
          return date.toLocaleTimeString('ko-KR', {
            timeZone: 'Asia/Seoul',
            hour: '2-digit', minute: '2-digit', hour12: false
          });
        },
      },
      rightPriceScale: { borderColor: 'transparent' },
      crosshair: {
        mode: 1,
        vertLine: { width: 1, color: themeColors.textColor, style: 3, labelBackgroundColor: themeColors.lineColor },
        horzLine: { color: themeColors.textColor, labelBackgroundColor: themeColors.lineColor },
      }
    });

    const newSeries = chart.addAreaSeries({
      lineColor: themeColors.lineColor,
      topColor: themeColors.areaTop,
      bottomColor: themeColors.areaBottom,
      lineWidth: 2,
    });

    chartRef.current = chart;
    seriesRef.current = newSeries;

    const handleResize = () => {
        if (chartRef.current) chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [isDarkMode, targetCoin]); 


  // 2. [핵심 로직] 데이터가 변할 때 실행되는 부분
  useEffect(() => {
    if (!seriesRef.current || !data || data.length === 0) return;

    // A. 대량의 데이터(History)가 처음 들어왔을 때 -> setData (통째로 교체)
    // 조건: 아직 차트에 데이터가 없거나(lastTimeRef가 0), 데이터 개수가 10개 이상 한꺼번에 들어왔을 때
    if (lastTimeRef.current === 0 || data.length > 10) {
        
        // 데이터 포맷팅 (중복 제거 및 정렬)
        const uniqueData = [...new Map(data.map(item => [item.time, item])).values()]
                            .sort((a, b) => a.time - b.time);
        
        seriesRef.current.setData(uniqueData);
        
        // 마지막 시간 기록 업데이트
        if (uniqueData.length > 0) {
            lastTimeRef.current = uniqueData[uniqueData.length - 1].time;
        }
    } 
    // B. 실시간 데이터 1개가 들어왔을 때 -> update (끝에 붙이기)
    else {
        const latestData = data[data.length - 1];
        try {
          if (latestData.time > lastTimeRef.current) {
            seriesRef.current.update(latestData);
            lastTimeRef.current = latestData.time;
          }
        } catch (e) {
            console.warn("Chart Update Error:", e);
        }
    }
  }, [data]); // data가 바뀔 때마다 실행됨

  return <div ref={chartContainerRef} className="w-full h-[450px] rounded-xl overflow-hidden" />;
};